import React, { useState } from 'react'
import { useDeleteFleet, useFleets } from '../hooks/useFleets'
import { fleetStrings, getLocale, Locale } from '../i18n/fleetI18n'
import CreateFleetModal from './CreateFleetModal'
import { Fleet } from '../types/fleet.types'
import { useToast } from '../../../common/providers/ToastProvider/useToast'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'
import ConfirmationModal from '../../../common/components/atom/ConfirmationModal/ConfirmationModal'
import { PermissionTooltip, useCapability } from '../../rbac'

interface FleetListProps {
  onSelect: (fleetId: string) => void
  locale?: Locale
}

const card: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 14,
  background: '#fff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
}

// F2 — fleet list with create + delete; click a fleet to open its detail.
const FleetList: React.FC<FleetListProps> = ({ onSelect, locale }) => {
  const strings = fleetStrings(locale ?? getLocale())
  const { fleetsQuery } = useFleets()
  const deleteMutation = useDeleteFleet()
  // RBAC : créer = editor+, supprimer (cascade profil + clear devices) = admin.
  const canCreate = useCapability('fleet.edit')
  const canDelete = useCapability('fleet.delete')
  const toast = useToast()
  const [createOpen, setCreateOpen] = useState(false)
  const [fleetToDelete, setFleetToDelete] = useState<Fleet | null>(null)

  const fleets = fleetsQuery.data?.data ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
          {strings.title}
        </h3>
        <PermissionTooltip allowed={canCreate}>
          <ButtonV2
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!canCreate}
            variant="contained"
            color="primary"
            size="medium"
          >
            {strings.createFleet}
          </ButtonV2>
        </PermissionTooltip>
      </div>

      {fleetsQuery.isLoading ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>…</div>
      ) : fleets.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>{strings.noFleets}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {fleets.map((f) => (
            <div key={f.fleet_id} style={card} onClick={() => onSelect(f.fleet_id)}>
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
                    {f.fleet_name}
                  </span>
                  {(!f.minigrid_ids || f.minigrid_ids.length === 0) && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: 10,
                        fontSize: 12,
                        background: '#fffbeb',
                        color: '#b45309',
                      }}
                    >
                      {strings.notSyncedNoMinigrid}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {f.member_dev_euis.length} {strings.members.toLowerCase()} ·{' '}
                  {strings.version} {f.version}
                </div>
              </div>
              <PermissionTooltip allowed={canDelete}>
                <ButtonV2
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (canDelete) setFleetToDelete(f) }}
                  disabled={!canDelete || deleteMutation.isLoading}
                  variant="outlined"
                  color="secondary"
                  size="small"
                >
                  {strings.delete}
                </ButtonV2>
              </PermissionTooltip>
            </div>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={fleetToDelete !== null}
        type="danger"
        title={strings.deleteConfirmTitle}
        message={fleetToDelete ? strings.deleteConfirmMessage(fleetToDelete.fleet_name) : ''}
        confirmText={strings.delete}
        cancelText={strings.cancel}
        onCancel={() => setFleetToDelete(null)}
        onConfirm={() => {
          const f = fleetToDelete
          setFleetToDelete(null)
          if (f) deleteMutation.mutate(
            { fleetId: f.fleet_id },
            {
              onSuccess: () => toast.success(strings.delete),
              onError: () => toast.error(strings.genericError),
            },
          )
        }}
      />

      <CreateFleetModal
        open={createOpen && canCreate}
        onClose={() => setCreateOpen(false)}
        onCreated={(fleet) => onSelect(fleet.fleet_id)}
        strings={strings}
      />
    </div>
  )
}

export default FleetList
